/**
 * Admin → Announce: compose a founder message that shows users a one-time
 * modal on their next visit. Broadcast (no emails) or targeted (list
 * emails). Body is markdown; optional CTA button. Below the form, every
 * announcement with an active toggle.
 *
 * Mirrors the other admin views — `["admin-announcements"]` query, mutate
 * + invalidate.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createAnnouncement,
  getAdminAnnouncements,
  setAnnouncementActive,
  type AdminAnnouncement,
} from "@/features/admin/lib/adminApi";
import { relativeTime } from "@/shared/format";

export function AnnouncementsView() {
  return (
    <div className="space-y-6">
      <ComposeForm />
      <AnnouncementList />
    </div>
  );
}

function ComposeForm() {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [emails, setEmails] = useState("");
  const [unresolved, setUnresolved] = useState<string[]>([]);

  const publish = useMutation({
    mutationFn: () =>
      createAnnouncement({
        title: title.trim(),
        body: body.trim(),
        ctaLabel: ctaLabel.trim() || undefined,
        ctaUrl: ctaUrl.trim() || undefined,
        audienceEmails: emails
          .split(/[\s,]+/)
          .map((e) => e.trim())
          .filter(Boolean),
      }),
    onSuccess: (created) => {
      setUnresolved(created.unresolvedEmails);
      setTitle("");
      setBody("");
      setCtaLabel("");
      setCtaUrl("");
      setEmails("");
      qc.invalidateQueries({ queryKey: ["admin-announcements"] });
    },
  });

  const canPublish = title.trim().length > 0 && body.trim().length > 0;

  return (
    <section className="rounded-xl border border-border bg-surface p-5 space-y-3">
      <h2 className="text-sm uppercase tracking-wider text-text-muted">
        New announcement
      </h2>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        className="w-full rounded-lg border border-border bg-bg/40 px-3 py-2 text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Body (markdown — **bold**, links, lists)"
        rows={5}
        className="w-full resize-y rounded-lg border border-border bg-bg/40 px-3 py-2 text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent"
      />
      <div className="flex gap-2">
        <input
          value={ctaLabel}
          onChange={(e) => setCtaLabel(e.target.value)}
          placeholder="CTA label (optional, e.g. Claim founder pricing)"
          className="flex-1 rounded-lg border border-border bg-bg/40 px-3 py-2 text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent"
        />
        <input
          value={ctaUrl}
          onChange={(e) => setCtaUrl(e.target.value)}
          placeholder="CTA url (/pricing or https://…)"
          className="flex-1 rounded-lg border border-border bg-bg/40 px-3 py-2 text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent font-mono text-xs"
        />
      </div>
      <div>
        <textarea
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder="Target emails (comma/space separated). Leave blank to broadcast to everyone."
          rows={2}
          className="w-full resize-y rounded-lg border border-border bg-bg/40 px-3 py-2 text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent font-mono text-xs"
        />
        <p className="text-[11px] text-text-muted/70 mt-1">
          {emails.trim() ? "Targeted — only these users see it." : "Broadcast — every user sees it once."}
        </p>
      </div>

      {unresolved.length > 0 && (
        <p className="text-xs text-amber-300">
          Published, but these emails didn't match a user (typo?):{" "}
          {unresolved.join(", ")}
        </p>
      )}
      {publish.isError && (
        <p className="text-xs text-danger">Publish failed. Try again.</p>
      )}

      <button
        type="button"
        disabled={!canPublish || publish.isPending}
        onClick={() => publish.mutate()}
        className="w-full py-3 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 transition disabled:opacity-40"
      >
        {publish.isPending ? "Publishing…" : "Publish"}
      </button>
    </section>
  );
}

function AnnouncementList() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-announcements"],
    queryFn: getAdminAnnouncements,
  });

  if (isLoading) return <p className="text-sm text-text-muted">Loading…</p>;
  if (isError || !data)
    return <p className="text-sm text-danger">Couldn't load announcements.</p>;
  if (data.length === 0)
    return <p className="text-sm text-text-muted">No announcements yet.</p>;

  return (
    <div className="space-y-3">
      {data.map((a) => (
        <AnnouncementRow key={a.id} a={a} />
      ))}
    </div>
  );
}

function AnnouncementRow({ a }: { a: AdminAnnouncement }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => setAnnouncementActive(a.id, !a.active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-announcements"] }),
  });

  const audience =
    a.audienceKind === "all"
      ? "Everyone"
      : a.audienceEmails.length > 0
        ? a.audienceEmails.join(", ")
        : "Targeted";

  return (
    <div
      className={[
        "rounded-xl border bg-surface p-4 flex items-start justify-between gap-3",
        a.active ? "border-border" : "border-border/40 opacity-60",
      ].join(" ")}
    >
      <div className="min-w-0">
        <div className="font-medium text-text">{a.title}</div>
        <div className="text-xs text-text-muted mt-0.5 line-clamp-2">{a.body}</div>
        <div className="text-[11px] text-text-muted/70 mt-1.5">
          {audience}
          {a.createdAt ? ` · ${relativeTime(a.createdAt)}` : ""}
          {a.ctaLabel ? ` · CTA: ${a.ctaLabel}` : ""}
        </div>
      </div>
      <button
        type="button"
        disabled={toggle.isPending}
        onClick={() => toggle.mutate()}
        className={[
          "shrink-0 rounded-lg text-xs font-medium px-3 py-1.5 transition disabled:opacity-40",
          a.active
            ? "bg-accent/15 text-accent hover:bg-accent/25"
            : "border border-border text-text-muted hover:text-text",
        ].join(" ")}
      >
        {a.active ? "Active" : "Inactive"}
      </button>
    </div>
  );
}
